import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import dayjs from "dayjs";
import useAutoRefresh from "../hooks/useAutoRefresh";
import {
  AlertTriangle,
  Calendar,
  CalendarPlus,
  CheckCircle,
  Clock,
  ListChecks,
  Users,
  XCircle,
} from "lucide-react";

const statusConfig = {
  pendente: { label: "Pendente", className: "badge badge-pending", icon: Clock },
  confirmado: { label: "Confirmado", className: "badge badge-ok", icon: CheckCircle },
  cancelado: { label: "Cancelado", className: "badge badge-cancel", icon: XCircle },
  finalizado: { label: "Finalizado", className: "badge badge-finalizado", icon: CheckCircle },
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);

  const carregarDashboard = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/dashboard");
      setDados(data);
    } catch (e) {
      console.error("Erro ao buscar dados:", e);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarDashboard(false);
  }, [carregarDashboard]);

  useEffect(() => {
    const handler = () => carregarDashboard(true);
    window.addEventListener("fz:agendamento", handler);
    window.addEventListener("fz:notificacao", handler);
    return () => {
      window.removeEventListener("fz:agendamento", handler);
      window.removeEventListener("fz:notificacao", handler);
    };
  }, [carregarDashboard]);

  useAutoRefresh(() => carregarDashboard(true), 10000, true);

  const proximosAgendamentos = useMemo(() => dados?.proximosAgendamentos || [], [dados]);
  const agendamentosAtivos = useMemo(
    () => proximosAgendamentos.filter((agenda) => agenda.status !== "cancelado"),
    [proximosAgendamentos]
  );

  const hoje = dayjs();
  const agendamentosHoje = useMemo(
    () =>
      agendamentosAtivos
        .filter((agenda) => dayjs(agenda.data_horario).isSame(hoje, "day"))
        .sort((a, b) => new Date(a.data_horario) - new Date(b.data_horario)),
    [agendamentosAtivos, hoje]
  );

  const contagemHojeCalculada = useMemo(() => {
    const base = { pendente: 0, confirmado: 0, cancelado: 0, finalizado: 0 };
    agendamentosHoje.forEach((agenda) => {
      const status = agenda.status || "pendente";
      base[status] = (base[status] || 0) + 1;
    });
    return base;
  }, [agendamentosHoje]);
  const resumoHoje = dados?.resumoHoje || {};
  const contagemHoje = {
    total: Number(resumoHoje.total ?? agendamentosHoje.length) || 0,
    pendente: Number(resumoHoje.pendente ?? contagemHojeCalculada.pendente) || 0,
    confirmado: Number(resumoHoje.confirmado ?? contagemHojeCalculada.confirmado) || 0,
    cancelado: Number(resumoHoje.cancelado ?? contagemHojeCalculada.cancelado) || 0,
    finalizado: Number(resumoHoje.finalizado ?? contagemHojeCalculada.finalizado) || 0,
  };

  const profissionaisHoje = useMemo(() => {
    const ids = new Set();
    agendamentosHoje.forEach((agenda) => {
      const id = agenda.profissional_id || agenda.profissional?.id || agenda.profissional?.nome;
      if (id) ids.add(id);
    });
    return ids.size;
  }, [agendamentosHoje]);

  if (loading) return <p className="muted">Carregando painel...</p>;
  if (!dados) return <p>Erro ao carregar dados.</p>;

  return (
    <div className="dashboard-page">
      <section className="dashboard-hero">
        <div>
          <span className="eyebrow">Operação de hoje</span>
          <h1>Dashboard</h1>
          <p>{hoje.format("DD/MM/YYYY")} · acompanhe os atendimentos e pendências do dia.</p>
        </div>

        <div className="dashboard-hero__actions">
          <button className="btn btn-outline" onClick={() => navigate("/agendamentos")}>
            <Calendar size={18} />
            Abrir calendário
          </button>
          <button className="btn" onClick={() => navigate("/agendamentos")}>
            <CalendarPlus size={18} />
            Novo agendamento
          </button>
        </div>
      </section>

      <section className="operations-grid">
        <KPI icon={Calendar} label="Hoje" value={contagemHoje.total} tone="orange" />
        <KPI icon={Clock} label="Pendentes" value={contagemHoje.pendente} tone="yellow" />
        <KPI icon={CheckCircle} label="Confirmados" value={contagemHoje.confirmado} tone="green" />
        <KPI icon={XCircle} label="Cancelados" value={contagemHoje.cancelado} tone="red" />
        <KPI icon={ListChecks} label="Finalizados" value={contagemHoje.finalizado} tone="slate" />
        <KPI icon={Users} label="Profissionais no dia" value={profissionaisHoje} tone="blue" />
      </section>

      <section className="dashboard-layout">
        <div className="card-block dashboard-panel">
          <div className="panel-title-row">
            <div>
              <span className="eyebrow">Linha do tempo</span>
              <h2>Agenda de hoje</h2>
            </div>
            <button className="btn btn-outline" onClick={() => navigate("/agendamentos")}>
              Ver agenda
            </button>
          </div>

          {agendamentosHoje.length ? (
            <div className="timeline-list">
              {agendamentosHoje.slice(0, 8).map((agenda) => (
                <TimelineItem key={agenda.id} agenda={agenda} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={Calendar}
              title="Nenhum atendimento hoje"
              text="Quando houver agendamentos para o dia, eles aparecem aqui em ordem de horário."
            />
          )}
        </div>

        <aside className="card-block dashboard-panel">
          <div className="panel-title-row">
            <div>
              <span className="eyebrow">Atenção</span>
              <h2>Pendências</h2>
            </div>
          </div>

          <div className="alert-stack">
            {contagemHoje.pendente > 0 ? (
              <div className="operational-alert operational-alert--warning">
                <AlertTriangle size={18} />
                <span>{contagemHoje.pendente} agendamento(s) aguardando confirmação hoje.</span>
              </div>
            ) : (
              <div className="operational-alert operational-alert--success">
                <CheckCircle size={18} />
                <span>Nenhuma pendência de confirmação para hoje.</span>
              </div>
            )}

            <div className="quick-list">
              <h3>Próximos agendamentos</h3>
              {agendamentosAtivos.slice(0, 5).map((agenda) => (
                <button key={agenda.id} type="button" onClick={() => navigate("/agendamentos")}>
                  <span>{dayjs(agenda.data_horario).format("DD/MM HH:mm")}</span>
                  <strong>{agenda.nome_cliente || agenda.cliente?.nome || "-"}</strong>
                </button>
              ))}
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
};

const KPI = ({ icon: Icon, label, value, tone }) => (
  <div className={`operation-card operation-card--${tone}`}>
    <div className="operation-card__icon">
      <Icon size={20} />
    </div>
    <div>
      <span>{label}</span>
      <strong>{value ?? 0}</strong>
    </div>
  </div>
);

const TimelineItem = ({ agenda }) => {
  const status = statusConfig[agenda.status] || statusConfig.pendente;
  const StatusIcon = status.icon;

  return (
    <div className="timeline-item">
      <time>{dayjs(agenda.data_horario).format("HH:mm")}</time>
      <div className="timeline-item__body">
        <strong>{agenda.nome_cliente || agenda.cliente?.nome || "-"}</strong>
        <span>
          {agenda.servico?.nome || "-"} · {agenda.profissional?.nome || "Profissional não informado"}
        </span>
      </div>
      <span className={status.className}>
        <StatusIcon size={12} />
        {status.label}
      </span>
    </div>
  );
};

const EmptyState = ({ icon: Icon, title, text }) => (
  <div className="empty-state">
    <Icon size={28} />
    <strong>{title}</strong>
    <p>{text}</p>
  </div>
);

export default Dashboard;
